import { gql } from "graphql-request";

export const CREATE_GROUP_MUTATION = gql`
  mutation InsertGroup(
    $name: String!
    $description: String
    $coverImageUrl: String
    $profileImageUrl: String
    $isPrivate: Boolean
  ) {
    insertgroup(
      input: {
        name: $name
        description: $description
        coverImageUrl: $coverImageUrl
        profileImageUrl: $profileImageUrl
        isPrivate: $isPrivate
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const CREATE_GROUP_MEMBERSHIP_MUTATION = gql`
  mutation InsertGroupMembership(
    $groupId: String!
    $userId: String!
    $role: String!
    $status: String!
    $joinType: String!
    $joinedAt: String!
    $invitedBy: String
    $groupUserSnapshot: usersnapshotInput!
  ) {
    insertgroupmembership(
      input: {
        groupId: $groupId
        userId: $userId
        role: $role
        status: $status
        joinType: $joinType
        invitedBy: $invitedBy
        groupUserSnapshot: $groupUserSnapshot
        joinedAt: $joinedAt
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

/**
 * Bulk membership insert — one request for the whole selection.
 *
 * Adding N people used to be N concurrent `insertgroupmembership` calls fired
 * from a single `Promise.all`. At a handful of members that is merely wasteful;
 * at the few hundred a country filter can select it is a partial-write hazard,
 * because a failure part-way leaves no record of which memberships landed.
 *
 * `totalImpactedData` is what makes the result checkable: compare it against the
 * number of rows sent and a short write is visible rather than silent.
 */
export const INSERT_MANY_GROUP_MEMBERSHIP_MUTATION = gql`
  mutation InsertManyGroupMembership($input: [groupmembershipInsertInput!]!) {
    insertManygroupmembership(input: $input) {
      acknowledged
      totalImpactedData
      message
      itemIds
    }
  }
`;

export const UPDATE_GROUP_MEMBERSHIP_MUTATION = gql`
  mutation UpdateGroupMembership(
    $itemId: String!
    $status: String
    $role: String
  ) {
    updategroupmembership(
      where: { ItemId: { eq: $itemId } }
      input: { status: $status, role: $role }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const DELETE_GROUP_MEMBERSHIP_MUTATION = gql`
  mutation DeleteGroupMembership($itemId: String!) {
    deletegroupmembership(
      where: { ItemId: { eq: $itemId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const DELETE_GROUP_MUTATION = gql`
  mutation DeleteGroup($itemId: String!) {
    deletegroup(
      where: { ItemId: { eq: $itemId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// Bulk delete: every membership belonging to a user (used by the user-deletion purge).
export const DELETE_MANY_GROUP_MEMBERSHIP_BY_USER = gql`
  mutation DeleteManyGroupMembershipByUser($userId: String!) {
    deleteManygroupmembership(
      where: { userId: { eq: $userId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Bulk update: refresh the member snapshot on every membership of this user
// after a profile edit (used by user-name-snapshot-sync).
export const UPDATE_MANY_GROUP_MEMBERSHIP_SNAPSHOT = gql`
  mutation UpdateManyGroupMembershipSnapshot(
    $userId: String!
    $firstName: String
    $lastName: String
    $email: String
    $profileImageUrl: String
  ) {
    updateManygroupmembership(
      where: { userId: { eq: $userId } }
      input: {
        groupUserSnapshot: {
          firstName: $firstName
          lastName: $lastName
          email: $email
          profileImageUrl: $profileImageUrl
        }
      }
    ) {
      acknowledged
      totalImpactedData
      message
    }
  }
`;

// Bulk delete: every membership of a group (used when deleting a group).
export const DELETE_MANY_GROUP_MEMBERSHIP_BY_GROUP = gql`
  mutation DeleteManyGroupMembershipByGroup($groupId: String!) {
    deleteManygroupmembership(
      where: { groupId: { eq: $groupId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Bulk delete: every group created by a user (used by the user-deletion purge).
export const DELETE_MANY_GROUP_BY_CREATOR = gql`
  mutation DeleteManyGroupByCreator($createdBy: String!) {
    deleteManygroup(
      where: { CreatedBy: { eq: $createdBy } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

export const GET_GROUPS_QUERY = gql`
  query GetGroups(
    $where: groupFilterInput
    $order: [DynamicSortInput!]
    $pageNo: Int
    $pageSize: Int
  ) {
    getgroups(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      totalCount
      totalPages
      hasNextPage
      hasPreviousPage
      items {
        ItemId
        name
        coverImageUrl
        profileImageUrl
        memberCount
        isPrivate
        description
        CreatedBy
        CreatedDate
      }
    }
  }
`;

export const GET_GROUP_MEMBERS_QUERY = gql`
  query GetGroupMembers(
    $where: groupmembershipFilterInput
    $order: [DynamicSortInput!]
    $pageNo: Int
    $pageSize: Int
  ) {
    getgroupmemberships(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      items {
        ItemId
        groupId
        userId
        role
        status
        joinType
        joinedAt
        groupUserSnapshot {
          firstName
          lastName
          email
          profileImageUrl
        }
      }
      totalCount
      totalPages
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const UPDATE_GROUP_MUTATION = gql`
  mutation UpdateGroup(
    $name: String
    $description: String
    $coverImageUrl: String
    $profileImageUrl: String
    $itemId: String!
    $isPrivate: Boolean
  ) {
    updategroup(
      where: { ItemId: { eq: $itemId } }
      input: {
        name: $name
        description: $description
        coverImageUrl: $coverImageUrl
        profileImageUrl: $profileImageUrl
        isPrivate: $isPrivate
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const GET_GROUP_MEMBER_COUNT = gql`
  query GetGroupMemberCount($where: groupmembershipFilterInput) {
    getgroupmemberships(where: $where) {
      totalCount
    }
  }
`;
